import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, StickyNote, Loader2, User as UserIcon } from 'lucide-react';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useTheme } from '../contexts/ThemeContext.tsx';
import api from '../services/api.ts';

interface SidebarNotesListProps {
  recordId?: number;
  contactId: string;
  contactPhone: string;
  clinicId: number;
}

interface Note {
  id: number;
  content: string;
  created_at: string;
  updated_at: string;
  created_by?: { name?: string };
}

const SidebarNotesList: React.FC<SidebarNotesListProps> = ({ contactId, recordId }) => {
  const resource = recordId ? `by-id/${recordId}` : encodeURIComponent(contactId);
  const { isDark } = useTheme();
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/contacts/${resource}/notes`);
      const data: Note[] = Array.isArray(res.data) ? res.data : [];
      const sorted = data.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      console.log('[SidebarNotesList] Notas carregadas:', sorted.length);
      setNotes(sorted);
    } catch (e) {
      console.error('[SidebarNotesList] Erro ao carregar notas:', e);
    } finally {
      setLoading(false);
    }
  }, [resource]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleSubmit = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/api/notes/${editingId}`, { content });
      } else {
        await api.post(`/api/contacts/${resource}/notes`, { content });
      }
      setContent('');
      setShowForm(false);
      setEditingId(null);
      await fetchNotes();
    } catch (e) {
      console.error('[SidebarNotesList] Erro ao salvar nota:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Excluir esta anotação?')) return;
    try {
      await api.delete(`/api/notes/${id}`);
      await fetchNotes();
    } catch (e) {
      console.error('[SidebarNotesList] Erro ao excluir nota:', e);
    }
  };

  const startEdit = (note: Note) => {
    setEditingId(note.id);
    setContent(note.content);
    setShowForm(true);
  };

  const startCreate = () => {
    setEditingId(null);
    setContent('');
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setContent('');
  };

  return (
    <div className="p-4 space-y-3">
      {!showForm && (
        <button
          onClick={startCreate}
          className="w-full rounded-xl bg-sminy-primary hover:bg-sminy-primary/90 px-4 py-2.5 text-sm font-medium text-white transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Nova anotação
        </button>
      )}

      {showForm && (
        <div
          className={`rounded-2xl border p-3 ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}
        >
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={5}
            placeholder="Digite sua anotação..."
            autoFocus
            className={`w-full p-3 border rounded-xl text-sm resize-none focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary transition-colors ${
              isDark
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
            }`}
          />
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={handleSubmit}
              disabled={!content.trim() || saving}
              className="flex-1 rounded-xl bg-sminy-primary hover:bg-sminy-primary/90 disabled:opacity-50 px-4 py-2 text-sm font-medium text-white transition-colors flex items-center justify-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {editingId ? 'Salvar' : 'Criar'}
            </button>
            <button
              onClick={cancelForm}
              className={`rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
                isDark
                  ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-4 border-sminy-primary/30 border-t-sminy-primary rounded-full animate-spin" />
        </div>
      ) : notes.length === 0 && !showForm ? (
        <div className="flex flex-col items-center justify-center py-8 px-4">
          <div
            className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-3 ${
              isDark ? 'bg-gray-700' : 'bg-gray-100'
            }`}
          >
            <StickyNote className={`w-8 h-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
          </div>
          <h3 className={`text-sm font-semibold mb-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
            Nenhuma anotação
          </h3>
          <p className={`text-xs text-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Crie a primeira anotação sobre este contato.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {notes.map((note) => {
            const date = parseISO(note.created_at);
            const wasEdited = note.updated_at !== note.created_at;
            return (
              <div
                key={note.id}
                className={`rounded-2xl border p-3 transition-colors ${
                  isDark
                    ? 'bg-gray-800 border-gray-700 hover:border-gray-600'
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
              >
                <p
                  className={`text-sm whitespace-pre-wrap break-words mb-2 ${
                    isDark ? 'text-gray-200' : 'text-gray-800'
                  }`}
                >
                  {note.content}
                </p>
                <div className="flex items-center justify-between">
                  <div
                    className={`text-xs flex items-center gap-1.5 flex-wrap ${
                      isDark ? 'text-gray-500' : 'text-gray-400'
                    }`}
                  >
                    {note.created_by?.name && (
                      <>
                        <UserIcon className="w-3 h-3" />
                        <span>{note.created_by.name}</span>
                        <span>·</span>
                      </>
                    )}
                    <span title={format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}>
                      {formatDistanceToNow(date, { locale: ptBR, addSuffix: true })}
                    </span>
                    {wasEdited && (
                      <>
                        <span>·</span>
                        <span className="italic">editado</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => startEdit(note)}
                      title="Editar"
                      className={`p-1.5 rounded-lg transition-colors ${
                        isDark
                          ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                          : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                      }`}
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(note.id)}
                      title="Excluir"
                      className={`p-1.5 rounded-lg transition-colors ${
                        isDark
                          ? 'text-red-400 hover:bg-red-900/30'
                          : 'text-red-500 hover:bg-red-50'
                      }`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SidebarNotesList;
